import { FilePdfOutlined } from '@ant-design/icons';
import { useReportActions } from '@/hooks/useReportActions';
import {
  TEFReceiptPrintTargetEnum,
  TEFReceiptPrintTypeEnum,
} from '@/types/Tef';
import { Button, Checkbox, Col, Radio, Row } from 'antd';
import { useState } from 'react';

import './style.less';

interface TEFProofOfPaymentProps<T = unknown> {
  entityId?: number;
  generateReceipt: (params: {
    entityId: number;
    printTarget: TEFReceiptPrintTargetEnum;
    printType: TEFReceiptPrintTypeEnum;
    showCustomerDocument?: boolean;
  }) => Promise<T>;
  fileName?: string;
  showCustomerDocument?: boolean;
}

type PrintOptions = {
  printTarget: TEFReceiptPrintTargetEnum;
  printType: TEFReceiptPrintTypeEnum;
  showCustomerDocument?: boolean;
};

export function TEFProofOfPayment<T = unknown>({
  entityId,
  generateReceipt,
  fileName = 'comprovante-tef',
  showCustomerDocument,
}: TEFProofOfPaymentProps<T>) {
  const [printOptions, setPrintOptions] = useState<PrintOptions>({
    printTarget: TEFReceiptPrintTargetEnum.Customer,
    printType: TEFReceiptPrintTypeEnum.Thermal,
    showCustomerDocument: false,
  });

  const { handleView, fileLoading } = useReportActions({
    fileName,
    generate: async () => {
      if (!entityId) return null;

      return generateReceipt({
        entityId,
        printTarget: printOptions.printTarget,
        printType: printOptions.printType,
        showCustomerDocument: printOptions.showCustomerDocument,
      });
    },
  });

  return (
    <Row gutter={[10, 10]} justify="end" className="proof-of-payment-container">
      <Col>
        <Radio.Group
          value={printOptions.printTarget}
          onChange={(event) =>
            setPrintOptions((prev) => ({
              ...prev,
              printTarget: event.target.value,
            }))
          }
          buttonStyle="solid"
        >
          <Radio.Button value={TEFReceiptPrintTargetEnum.Customer}>
            Cliente
          </Radio.Button>

          <Radio.Button value={TEFReceiptPrintTargetEnum.Establishment}>
            Estabelecimento
          </Radio.Button>
        </Radio.Group>
      </Col>

      <Col>
        <Radio.Group
          value={printOptions.printType}
          onChange={(event) =>
            setPrintOptions((prev) => ({
              ...prev,
              printType: event.target.value,
            }))
          }
          buttonStyle="solid"
        >
          <Radio.Button value={TEFReceiptPrintTypeEnum.Thermal}>
            Térmica (máquina)
          </Radio.Button>

          <Radio.Button value={TEFReceiptPrintTypeEnum.A4}>
            A4 (folha)
          </Radio.Button>
        </Radio.Group>
      </Col>

      {showCustomerDocument && (
        <Col>
          <Checkbox
            checked={printOptions.showCustomerDocument}
            onChange={(event) =>
              setPrintOptions((prev) => ({
                ...prev,
                showCustomerDocument: event.target.checked,
              }))
            }
            defaultChecked={false}
          >
            Exibir CPF do cliente
          </Checkbox>
        </Col>
      )}

      <Col>
        <Button
          type="primary"
          block
          icon={<FilePdfOutlined />}
          loading={fileLoading.REPORT_VIEW}
          onClick={handleView}
        >
          Gerar comprovante em PDF
        </Button>
      </Col>
    </Row>
  );
}
